import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card } from './UI';

interface LoginModalProps {
  onClose?: () => void;
}

const LoginModal = ({ onClose }: LoginModalProps) => {
  const navigate = useNavigate();
  const { login, loginWithToken } = useAuth();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 開発環境かどうかを判定
  const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_API_URL?.includes('dev');

  // 電話番号を国際形式に変換するヘルパー関数
  const formatPhoneNumber = (phone: string): string => {
    // 日本の電話番号を国際形式に変換
    const cleaned = phone.replace(/[-\s()]/g, ''); // ハイフンやスペースを除去
    if (cleaned.startsWith('0')) {
      return `+81${cleaned.slice(1)}`; // 先頭の0を81に置換
    }
    if (cleaned.startsWith('+81')) {
      return cleaned; // 既に国際形式
    }
    return `+81${cleaned}`; // その他の場合は+81を追加
  };

  const handleSendSMS = async () => {
    if (!phoneNumber) {
      setError('電話番号を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/auth/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber: formattedPhoneNumber }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStep('code');
      } else {
        // Handle different error response formats
        let errorMessage = 'SMS送信に失敗しました';

        if (result.error) {
          if (typeof result.error === 'string') {
            errorMessage = result.error;
          } else if (result.error.message) {
            errorMessage = result.error.message;
          }
        } else if (result.message) {
          errorMessage = result.message;
        } else if (!response.ok) {
          errorMessage = `サーバーエラー (${response.status})`;
        }

        setError(errorMessage);
      }
    } catch (_error) {
      setError('SMS送信中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!code) {
      setError('認証コードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
      const success = await login(formattedPhoneNumber, code);
      if (success) {
        if (onClose) {
          onClose();
        } else {
          // ログイン成功後、現在のページをリロード
          window.location.reload();
        }
      } else {
        setError('認証に失敗しました。正しいコードを入力してください。');
      }
    } catch (_error) {
      setError('ログイン中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/');
    }
  };

  // テストユーザーでログイン
  const handleTestLogin = async (userId: string) => {
    setIsLoading(true);
    setError('');

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${apiUrl}/api/auth/test-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // トークンを使ってログイン
        await loginWithToken(result.accessToken, result.refreshToken, result.user);

        if (onClose) {
          onClose();
        } else {
          window.location.reload();
        }
      } else {
        setError(result.error || 'テストログインに失敗しました');
      }
    } catch (_error) {
      setError('テストログイン中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 p-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ログインが必要です</h2>
          <p className="text-gray-600">
            {step === 'phone'
              ? '家庭教師プロフィール作成・募集投稿にはログインが必要です。電話番号を入力してください。'
              : 'SMSで送信された認証コードを入力してください。'}
          </p>
        </div>

        {step === 'phone' ? (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="phone-number"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                電話番号
              </label>
              <input
                id="phone-number"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="例: 09012345678"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1"
              >
                キャンセル
              </Button>
              <Button onClick={handleSendSMS} disabled={isLoading} className="flex-1">
                {isLoading ? 'SMS送信中...' : 'SMS送信'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="auth-code" className="block text-sm font-medium text-gray-700 mb-1">
                認証コード
              </label>
              <input
                id="auth-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="例: 123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
                maxLength={6}
              />
            </div>

            {error && <p className="text-red-600 text-sm">{error}</p>}

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setStep('phone')}
                disabled={isLoading}
                className="flex-1"
              >
                戻る
              </Button>
              <Button onClick={handleLogin} disabled={isLoading} className="flex-1">
                {isLoading ? 'ログイン中...' : 'ログイン'}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            新規ユーザーの場合は、SMSでの認証後に自動的にアカウントが作成されます。
          </p>
        </div>

        {/* 開発環境でのテストログインボタン */}
        {isDevelopment && step === 'phone' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3 text-center">開発環境用テストログイン</p>
            <div className="space-y-2">
              <Button
                variant="outline"
                onClick={() => handleTestLogin('test_tutor')}
                disabled={isLoading}
                className="w-full"
              >
                テスト家庭教師でログイン
              </Button>
              <Button
                variant="outline"
                onClick={() => handleTestLogin('test_parent')}
                disabled={isLoading}
                className="w-full"
              >
                テスト保護者でログイン
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LoginModal;
